import React from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/core"
import { useCookies } from "react-cookie"
import { NavLink } from "react-router-dom"

// Import font files
import SuisseIntlLightWoff2 from './fonts/SuisseIntl-Light.woff2'
import SuisseIntlLightWoff from './fonts/SuisseIntl-Light.woff'
import SuisseIntlRegularWoff2 from './fonts/SuisseIntl-Regular.woff2'
import SuisseIntlRegularWoff from './fonts/SuisseIntl-Regular.woff'
import NoeDisplayRegularWoff2 from './fonts/NoeDisplay-Regular.woff2'
import NoeDisplayRegularWoff from './fonts/NoeDisplay-Regular.woff'

export const globalStyles = css`
    *,
    *:focus {
        box-sizing: border-box;
        outline: none;
        font-family: "Noe Display", sans-serif;
    }
    #root {
        width: 100%;
        max-width: 1184px;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: auto auto;
    }

    @font-face {
        font-family: "Suisse Int'l";
        src: url(${SuisseIntlLightWoff2}) format("woff2"),
             url(${SuisseIntlLightWoff}) format("woff");
        font-weight: 400;
        font-style: normal;
        font-display: swap;
    }

    @font-face {
        font-family: "Suisse Int'l";
        src: url(${SuisseIntlRegularWoff2}) format("woff2"),
             url(${SuisseIntlRegularWoff}) format("woff");
        font-weight: 600;
        font-style: normal;
        font-display: swap;
    }

    @font-face {
        font-family: "Noe Display";
        src: url(${NoeDisplayRegularWoff2}) format("woff2"),
             url(${NoeDisplayRegularWoff}) format("woff");
        font-weight: normal;
        font-style: normal;
        font-display: swap;
    }
`

const LoaderWrapper = styled("div")`
    background: rgba(255, 255, 255, 0.1);
    position: absolute;
    z-index: 999;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 2rem;
    text-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
    transition: 0.3s;
    &.slow {
        transform: scale(1.5);
        color: green;
        background: rgba(255, 255, 255, 0.3);
    }
`

export const SelectButton = styled("button")`
    background: none;
    width: 23.5%;
    border: none;
    display: flex;
    transition: 0.3s;
    align-items: center;
    height: 90px;
    padding: 0;
    margin-bottom: 1rem;
    border: 1px solid #D3D0CB;
    text-align: left;
    margin-right: 2%;
    position: relative;
    &:nth-of-type(4n) {
        margin-right: 0;
    }
    cursor: pointer;
    img {
        max-height: 100%;
        background: #D3D0CB;
        width: auto;
        align-self: center;
        flex: 0 0 auto;
        margin-right: 1rem;
        min-width: 90px;
    }
    h3 {
        flex-grow: 1;
        flex-basis: 100%;
        margin: 0;
        span {
            display: block;
            font-size: 0.7em;
            font-weight: 100;
        }
    }
    svg {
        width: 100%;
        max-width: 1rem;
        margin: 0 1rem;
        flex-grow: 1;
        path {
            transition: 0.3s;
            fill: transparent;
        }
    }

    @media (max-width: 600px) {
        min-width: 48%;
        margin-right: 4%;
        height: 50px;
        &:nth-of-type(2n) {
            margin-right: 0;
        }
        h3 {
            font-weight: 400;
            font-size: 11px;
        }
        img {
            max-width: 50px;
            min-width: 50px;
            width: 50px;
            height: 50px;
        }
    }

    &:hover {
        border-color: #6A6560;
    }
    &.active {
        svg {
            path {
                fill: black;
            }
        }
    }
    svg.zoomImage {
        position: absolute;
        top: 8px;
        left: 8px;
        display: block !important;
        margin: 0;
        height: 1rem;
        overflow: visible;
        path {
            fill: white;
        }
    }
`

export const Loader = ({ refetched }) => (
    <LoaderWrapper className={refetched ? "slow" : "normal"}>
        <i className="fas fa-circle-notch fa-spin" />
    </LoaderWrapper>
)

export const StageWrapper = styled("div")`
    background: white
        url(${(props) => (props.background ? props.background : "")});
    background-size: 100% auto;
    max-width: 1184px;
    height: 0;
    padding-bottom: 56.25%;
    width: 100%;
    position: relative;
    img {
        position: absolute;
        top: 0;
        left: 0;
        max-width: 1184px;
        width: 100%;
        height: auto;
    }
`

export const TabBar = styled("div")`
    display: flex;
    background: #D3D0CB;
    max-width: 1184px;
    width: 100%;
    z-index: 99;
    @media (max-width: 1184px) {
        overflow-x: scroll;
    }
`

export const Tab = styled(NavLink)`
    text-align: center;
    flex-grow: 1;
    padding: 1rem;
    background: #D3D0CB;
    text-decoration: none;
    font-size: 14px;

    letter-spacing: 0.2ch;
    transition: 0.3s;
    color: black;
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
        width: 1rem;
        margin-left: 0.5rem;
    }
    &.active,
    &:hover {
        background: #6A6560;
        color: white;
    }
    @media (max-width: 1184px) {
        padding: 0.5rem 0.75rem;
        font-size: 11px;
        white-space: nowrap;
    }
`

export const ActiveTab = styled("div")`
    min-height: 200px;
    max-width: 1184px;
    width: 100%;
    padding: 1rem 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    position: relative;
`

const ModalWrapper = styled("div")`
    position: fixed;
    top: 0;
    width: 100vw;
    height: 100vh;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99;
`

const Overlay = styled("div")`
    position: absolute;
    top: 0;
    width: 100vw;
    height: 100vh;
    left: 0;
    background: rgba(0, 0, 0, 0.2);
`

const Box = styled("div")`
    position: relative;
    width: 100%;
    max-width: 420px;
    background: white;
    padding: 2rem;
    text-align: center;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
    z-index: 9;
    h1 {
        text-transform: uppercase;
        font-size: 1.3rem;
    }
`

export const Close = styled("button")`
    background: #D3D0CB;
    border: none;
    padding: 0.5rem 1rem;
    font-size: 1rem;
    text-transform: uppercase;
`

export const ModalBox = ({ children, closeBox }) => {
    return (
        <ModalWrapper>
            <Overlay onClick={closeBox} />
            <Box>
                {children}
                <Close onClick={closeBox}>Close</Close>
            </Box>
        </ModalWrapper>
    )
}

export const LegTypesWrapper = styled("div")`
    display: flex;
    width: 100%;
    margin-top: -0.5rem;
    margin-bottom: 1rem;
    border-bottom: 1px solid #D3D0CB;

    button {
        border: none;
        padding: 1rem;
        width: 160px;
        max-width: 33.3%;
        position: relative;
        &::after {
            transition: 0.3s;
            content: "";
            width: 100%;
            height: 0px;
            position: absolute;
            bottom: 0;
            left: 0;
        }
        &.active,
        &:hover {
            &::after {
                content: "";
                height: 5px;
                background: #6A6560;
            }
        }
    }
`

export const OffsetImage = styled("img")`
    transition: 0.3s;
    transform: translateY(${(props) => (props.offset ? props.offset : 0)}%) translateX(${(props) => (props.voffset ? props.voffset : 0)}%);
    &.AddOn-pop{
        z-index: 3;
    }
`

export const InfoWrapper = styled("div")`
    dt {
        margin-top: 1rem;
        font-weight: 700;
        text-transform: uppercase;
    }
    dd {
        margin-left: 0;
        margin: 3px 0;
    }

    &:hover,
    &.active {
        span.nowrap {
            height: 1rem;
            span {
                width: calc(100% - 1.5rem);
                opacity: 1;
                margin-left: 0.5rem;
            }
        }
    }
    button {
        width: 100%;
        max-width: 120px;
        background: black;
        color: #fff;
        padding: 1rem;
        border: 0;
        text-transform: uppercase;
    }
    @media (max-width: 1184px) {
        &.active {
            max-width: 100%;
            width: calc(100vw - 2rem);
            height: calc(100vh - 2rem);
            border-radius: 4px;
        }
    }
`

export const DebugBeds = styled("div")`
    width: 100%;
    background: #D3D0CB;
    display: flex;
    flex-wrap: wrap;
    a {
        font-weight: bold;
        color: black;
        text-decoration: none;
        text-transform: uppercase;
        display: block;
        padding: 1rem;
        min-width: 100px;
        width: 25%;
        &:hover {
            color: white;
            background: #6A6560;
        }
    }
`

export const Trans = ({ children }) => {
    const [cookies] = useCookies(["byob-lang"])
    const data = {
        nb: {
            Style: "Stil",
            Textile: "Tekstil",
            Topper: "Overmadrass",
            Leg: "Ben",
            Legs: "Ben",
            Headboard: "Hodegavl",
            Headboards: "Hodegavl",
            "Bedside table": "Nattbord",
            "Bedside tables": "Nattbord",
            Ottoman: "Ottoman",
            "Add ons": "Add ons",
            Overview: "Sammendrag",
            Split: "Delt",
            Bed: "Seng",
        },
        se: {
            Style: "Stil",
            Textile: "Textil",
            Topper: "Bäddmadrass",
            Leg: "Ben",
            Legs: "Ben",
            Headboard: "Huvudgavel",
            Headboards: "Huvudgavel",
            "Bedside table": "Nattduksbord",
            "Bedside tables": "Nattduksbord",
            Ottoman: "Ottoman",
            "Add ons": "Add ons",
            Overview: "Samanställning",
            Split: "Delad",
            Bed: "Säng",
        },
    }
    if (
        cookies["byob-lang"] in data &&
        children in data[cookies["byob-lang"]]
    ) {
        return data[cookies["byob-lang"]][children]
    }

    return children
}
